import { withAppContext } from '@/config/site'
import request from '@/utils/request'

const toSafeString = (value) => {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value) && value !== 0) return String(value)
  return ''
}

const toSafeNumber = (value, fallback = 0) => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : fallback
}

const parseMaybeJsonObject = (value) => {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  if (typeof value !== 'string') return {}

  try {
    const parsedValue = JSON.parse(value)
    return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
      ? parsedValue
      : {}
  } catch (error) {
    return {}
  }
}

const decodeHtmlEntities = (value) => {
  const text = toSafeString(value)
  if (!text) return ''

  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }

  return text
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
}

const getFirstString = (...values) => {
  for (const value of values) {
    const normalizedValue = toSafeString(value)
    if (normalizedValue) return normalizedValue
  }
  return ''
}

const normalizeVideoUrl = (value) => {
  const url = toSafeString(value)
  if (!url) return ''

  try {
    const base = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost'
    const parsed = new URL(url, base)
    return `${parsed.origin}${parsed.pathname}`
  } catch (error) {
    return url.split('#')[0].split('?')[0]
  }
}

const getDataAttr = (tag, name) => {
  const match = tag.match(new RegExp(`${name}=(?:"([^"]*)"|'([^']*)')`, 'i'))
  return decodeHtmlEntities(match ? (match[1] || match[2] || '') : '')
}

const extractLessonVideoFromContent = (content = '') => {
  const html = toSafeString(content)
  if (!html) return {}

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const block = doc.querySelector('.chapter-cloud-video-block, .chapter-cloud-file-block[data-resource-type="video"]')

    if (block) {
      const dataset = block.dataset || {}
      const videoElement = block.querySelector('video')

      return {
        video_id: getFirstString(dataset.videoFileId, dataset.resourceId),
        video_url: normalizeVideoUrl(decodeHtmlEntities(getFirstString(
          dataset.videoUrl,
          dataset.resourceUrl,
          videoElement && (videoElement.getAttribute('src') || videoElement.src)
        ))),
        video_duration: toSafeNumber(dataset.videoDuration, 0),
        video_resolution: getFirstString(dataset.videoResolution),
        video_size: toSafeNumber(dataset.resourceSize, 0),
        video_sd_url: normalizeVideoUrl(decodeHtmlEntities(getFirstString(dataset.videoSdUrl))),
        video_hd_url: normalizeVideoUrl(decodeHtmlEntities(getFirstString(dataset.videoHdUrl))),
        video_fhd_url: normalizeVideoUrl(decodeHtmlEntities(getFirstString(dataset.videoFhdUrl))),
        video_player_app_id: getFirstString(dataset.videoPlayerAppId),
        video_player_psign: getFirstString(dataset.videoPlayerPsign)
      }
    }
  }

  const blockMatch = html.match(/<[^>]+(?:chapter-cloud-video-block|data-resource-type=(?:"video"|'video'))[^>]*>/i)
  const blockTag = blockMatch ? blockMatch[0] : ''
  if (!blockTag) return {}

  return {
    video_id: getFirstString(
      getDataAttr(blockTag, 'data-video-file-id'),
      getDataAttr(blockTag, 'data-resource-id')
    ),
    video_url: getFirstString(
      getDataAttr(blockTag, 'data-video-url'),
      getDataAttr(blockTag, 'data-resource-url')
    ),
    video_duration: toSafeNumber(getDataAttr(blockTag, 'data-video-duration'), 0),
    video_resolution: getFirstString(getDataAttr(blockTag, 'data-video-resolution')),
    video_size: toSafeNumber(getDataAttr(blockTag, 'data-resource-size'), 0),
    video_sd_url: getFirstString(getDataAttr(blockTag, 'data-video-sd-url')),
    video_hd_url: getFirstString(getDataAttr(blockTag, 'data-video-hd-url')),
    video_fhd_url: getFirstString(getDataAttr(blockTag, 'data-video-fhd-url')),
    video_player_app_id: getFirstString(getDataAttr(blockTag, 'data-video-player-app-id')),
    video_player_psign: getFirstString(getDataAttr(blockTag, 'data-video-player-psign'))
  }
}

const isProbablyTruncatedVideoUrl = (url) => {
  const value = toSafeString(url)
  if (!value) return false

  return value.length >= 490 ||
    /%[0-9a-f]?$/i.test(value) ||
    /[?&](respo|response-content-|q-|x-amz-|expires|signature|key-pair-id|policy)$/i.test(value)
}

const chooseVideoUrl = (directUrl, contentUrl) => {
  const normalizedDirectUrl = decodeHtmlEntities(directUrl)
  const normalizedContentUrl = decodeHtmlEntities(contentUrl)

  if (!normalizedDirectUrl) return normalizedContentUrl
  if (!normalizedContentUrl) return normalizedDirectUrl
  if (normalizedContentUrl.startsWith(normalizedDirectUrl)) return normalizedContentUrl
  if (isProbablyTruncatedVideoUrl(normalizedDirectUrl) && normalizedContentUrl.length > normalizedDirectUrl.length) {
    return normalizedContentUrl
  }

  return normalizedDirectUrl
}

const normalizeLessonDetailVideo = (lesson = {}) => {
  if (!lesson || typeof lesson !== 'object') return {}

  const config = parseMaybeJsonObject(lesson.config)
  const contentVideo = extractLessonVideoFromContent(lesson.content)
  const directVideoUrl = getFirstString(
    lesson.video_url,
    lesson.videoUrl,
    lesson.play_url,
    lesson.playUrl,
    lesson.file_url,
    lesson.fileUrl,
    lesson.resource_url,
    lesson.resourceUrl,
    config.video_url,
    config.videoUrl,
    config.play_url,
    config.playUrl,
    config.file_url,
    config.fileUrl,
    config.resource_url,
    config.resourceUrl
  )
  const contentVideoUrl = getFirstString(
    contentVideo.video_url,
    contentVideo.video_sd_url,
    contentVideo.video_hd_url,
    contentVideo.video_fhd_url
  )

  return {
    ...lesson,
    config,
    original_video_url: lesson.video_url || '',
    video_id: getFirstString(
      lesson.video_id,
      lesson.video_file_id,
      lesson.videoFileId,
      lesson.file_id,
      lesson.fileId,
      config.video_id,
      config.video_file_id,
      config.videoFileId,
      config.file_id,
      config.fileId,
      contentVideo.video_id
    ),
    video_url: normalizeVideoUrl(chooseVideoUrl(directVideoUrl, contentVideoUrl)),
    video_duration: toSafeNumber(lesson.video_duration || config.video_duration || contentVideo.video_duration, 0),
    video_resolution: getFirstString(lesson.video_resolution, config.video_resolution, contentVideo.video_resolution),
    video_size: toSafeNumber(lesson.video_size || config.video_size || contentVideo.video_size, 0),
    video_sd_url: normalizeVideoUrl(chooseVideoUrl(getFirstString(lesson.video_sd_url, config.video_sd_url), contentVideo.video_sd_url)),
    video_hd_url: normalizeVideoUrl(chooseVideoUrl(getFirstString(lesson.video_hd_url, config.video_hd_url), contentVideo.video_hd_url)),
    video_fhd_url: normalizeVideoUrl(chooseVideoUrl(getFirstString(lesson.video_fhd_url, config.video_fhd_url), contentVideo.video_fhd_url)),
    video_player_app_id: getFirstString(
      lesson.video_player_app_id,
      lesson.videoPlayerAppId,
      config.video_player_app_id,
      config.videoPlayerAppId,
      contentVideo.video_player_app_id
    ),
    video_player_psign: getFirstString(
      lesson.video_player_psign,
      lesson.videoPlayerPsign,
      config.video_player_psign,
      config.videoPlayerPsign,
      contentVideo.video_player_psign
    )
  }
}

export const fetchCourseList = async (payload = {}) => {
  const response = await request('/page/course/index', {
    data: withAppContext({
      page: 1,
      pageSize: 9,
      ...payload
    })
  })

  return {
    current_page: Number(response.current_page) || 1,
    data: Array.isArray(response.data) ? response.data : [],
    from: response.from,
    last_page: Number(response.last_page) || 1,
    per_page: Number(response.per_page) || 0,
    to: response.to,
    total: Number(response.total) || 0
  }
}

export const fetchCourseDetail = async (id, payload = {}) => {
  const response = await request('/page/course/detail', {
    data: withAppContext({
      id,
      ...payload
    })
  })

  return response.data || {}
}

// 获取课程学习历史列表
export const fetchCourseHistory = async (payload = {}) => {
  const response = await request('/page/course/history/index', {
    data: withAppContext({
      page: 1,
      pageSize: 10,
      ...payload
    })
  })

  return {
    current_page: Number(response.current_page) || 1,
    data: Array.isArray(response.data) ? response.data : [],
    from: response.from,
    last_page: Number(response.last_page) || 1,
    per_page: Number(response.per_page) || 0,
    to: response.to,
    total: Number(response.total) || 0
  }
}

// 删除课程学习历史
export const removeCourseHistory = async (id) => {
  const response = await request('/page/course/history/remove', {
    data: withAppContext({
      id
    })
  })

  return response
}

export const fetchCourseEnrollmentStatus = async (courseId) => {
  const response = await request('/page/course/enrollment/status', {
    data: withAppContext({ course_id: courseId })
  })

  return response.data || {}
}

export const joinCourse = async (courseId) => {
  const response = await request('/page/course/enrollment/join', {
    data: withAppContext({ course_id: courseId, source: 'web' })
  })

  return response.data || {}
}

export const leaveCourse = async (courseId) => {
  const response = await request('/page/course/enrollment/leave', {
    data: withAppContext({ course_id: courseId, source: 'web' })
  })

  return response.data || {}
}

const normalizePagination = (response) => ({
  current_page: Number(response.current_page) || 1,
  data: Array.isArray(response.data) ? response.data : [],
  from: response.from,
  last_page: Number(response.last_page) || 1,
  per_page: Number(response.per_page) || 0,
  to: response.to,
  total: Number(response.total) || 0
})

export const fetchCourseEnrollmentList = async (payload = {}) => {
  const response = await request('/page/course/enrollment/index', {
    data: withAppContext({
      page: 1,
      pageSize: 20,
      ...payload
    })
  })

  return normalizePagination(response)
}

export const fetchCourseEnrollmentLogs = async (payload = {}) => {
  const response = await request('/page/course/enrollment/logs', {
    data: withAppContext({
      page: 1,
      pageSize: 20,
      ...payload
    })
  })

  return normalizePagination(response)
}

// 获取课时详情（用于视频播放页面）
export const fetchLessonDetail = async (id, payload = {}) => {
  const response = await request('/page/course/lesson-detail', {
    data: withAppContext({
      chapter_id: id,
      ...payload
    })
  })

  return normalizeLessonDetailVideo(response.data || {})
}
